// CALogin.js
import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const CALogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // Check if user has CA role
      const userRef = ref(database, `HTAMS/users/${user.uid}`);
      const userSnapshot = await get(userRef);

      if (userSnapshot.exists()) {
        const userData = userSnapshot.val();

        // Check if user is CA or admin
        if (userData.role === 'ca' || userData.role === 'admin') {
          localStorage.setItem('caUser', JSON.stringify({
            uid: user.uid,
            email: userData.email,
            name: userData.name,
            role: userData.role
          }));

          toast.success('CA Login Successful!');
          navigate('/ca-dashboard');
        } else {
          toast.error('Access Denied! CA privileges required.');
          await auth.signOut();
        }
      } else {
        toast.error('User data not found!');
        await auth.signOut();
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          * {
            box-sizing: border-box;
          }
          
          body {
            margin: 0;
            overflow-x: hidden;
          }
          
          .ca-login-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            padding: 20px;
            flex-direction: column;
            gap: 20px;
            position: relative;
            width: 100%;
          }

          .ca-login-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 420px;
            gap: 20px;
          }

          .ca-login-form {
            background-color: #ffffff;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
            width: 100%;
            box-sizing: border-box;
          }

          .ca-login-title {
            font-size: 2rem;
            font-weight: 700;
            margin-bottom: 30px;
            text-align: center;
            color: #1f2937;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-label {
            display: block;
            margin-bottom: 8px;
            font-size: 0.9rem;
            font-weight: 600;
            color: #374151;
          }

          .ca-login-error {
            color: #ef4444;
            margin-bottom: 20px;
            text-align: center;
            font-size: 0.875rem;
            background-color: #fef2f2;
            padding: 12px;
            border-radius: 8px;
            border: 1px solid #fecaca;
            line-height: 1.4;
          }

          .ca-login-success {
            color: #10b981;
            margin-bottom: 20px;
            text-align: center;
            font-size: 0.875rem;
            background-color: #ecfdf5;
            padding: 12px;
            border-radius: 8px;
            border: 1px solid #a7f3d0;
            line-height: 1.4;
          }

          .ca-login-input {
            box-sizing: border-box;
            width: 100%;
            padding: 14px 16px;
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            font-size: 1rem;
            transition: border-color 0.15s ease, box-shadow 0.15s ease;
            background-color: #f9fafb;
          }

          .ca-login-input:focus {
            outline: none;
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            background-color: #ffffff;
          }

          .ca-login-input:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .ca-login-button {
            width: 100%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 14px;
            border-radius: 10px;
            border: none;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            transition: opacity 0.15s ease, transform 0.15s ease;
            margin-bottom: 20px;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
          }

          .ca-login-button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
          }

          .ca-login-button:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
            pointer-events: none;
          }

          .button-spinner {
            width: 18px;
            height: 18px;
            border: 2px solid rgba(255,255,255,0.3);
            border-top: 2px solid white;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .ca-login-footer {
            font-size: 0.9rem;
            text-align: center;
            color: #374151;
            margin: 0;
          }

          @media (max-width: 480px) {
            .ca-login-wrapper {
              padding: 10px;
            }
            
            .ca-login-form {
              padding: 30px 20px;
              width: 100%;
            }
            
            .ca-login-title {
              font-size: 1.7rem;
            }
          }

          .ca-login-button:focus,
          .ca-login-input:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }
        `}
      </style>

      <div className="ca-login-wrapper">
        <div className="ca-login-container">
          <form onSubmit={handleSubmit} className="ca-login-form">
            <h2 className="ca-login-title">CA Portal Login</h2>
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="ca-login-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your CA email"
                disabled={loading}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                className="ca-login-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={loading}
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="ca-login-button"
              disabled={loading}
            >
              {loading && <div className="button-spinner"></div>}
              {loading ? 'Authenticating...' : 'Secure Login'}
            </button>

            <p className="ca-login-footer">
              🔒 Authorized CA Access Only
            </p>
          </form>
        </div>
      </div>
    </>
  );
};

export default CALogin;